import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  AlertTriangle,
  FileText,
  Image as ImageIcon,
  Link2,
  Plus,
  Sparkles,
  Wand2,
  X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/Spinner';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import { useElapsedSeconds } from '../../lib/useElapsedSeconds';
import {
  CAMPAIGN_COPY_FIELDS,
  campaignCopyField,
  campaignCopyLensBlurb,
  campaignCopyLensLabel,
  type CampaignCopyFieldId,
} from '../../lib/campaignCopy/fields';
import { planCampaignCopyInsert } from '../../lib/campaignCopy/insert';
import type {
  CampaignCopyOption,
  CampaignCopyResult,
} from '../../lib/campaignCopy/requests';
import type { AssetLabel } from '../../types/campaign';
import {
  describeOfferFacts,
  offerFactsAreStale,
} from '../../lib/campaignCopy/offerFacts';
import { isDriveLink } from '../../lib/campaignCopy/driveLinks';
import type { CampaignOfferFacts } from '../../types/campaign';
import { distilOfferFacts, generateCampaignCopy, refineCampaignCopy } from './api';
import {
  COPY_FIELD_ORDER,
  copyWaitLabel,
  describeBudget,
  describeOptionLength,
  describeReplacement,
  groupOptionsByField,
  optionIsTight,
} from './campaignCopyView';

// FRANCHISE CAMPAIGN COPY — the workspace.
//
// A focused MODAL, unlike the post-caption workspace, which is deliberately a
// full-width inline region. The difference is what the two are for: a caption is
// ONE piece of copy landing in the box directly below it, so hiding the composer
// would hide the destination. This writes FIVE fields spread down a long form,
// so there is no single destination to keep in view, and the AM is doing one
// concentrated job — picking copy — rather than editing around it.
//
// ⚠️⚠️ ON-DEMAND ONLY. No useEffect, no useQuery: every call fires from a click.
// The campaign form re-renders on every keystroke of a long authoring session,
// so a generation wired to a query would bill for all of it while looking like
// the feature simply worked well. Pinned by the source-shape guard
// campaignCopyOnDemandOnly.test.ts. (The wait timer is a legitimate effect and
// lives in src/lib/useElapsedSeconds.ts precisely so this file has none.)

const MAX_REFERENCE_LINKS = 5;

export interface CampaignCopyValues {
  metaBodyCopy: string[];
  metaHeadlines: string[];
  metaDescriptions: string[];
  googleHeadlines: string[];
  googleDescriptions: string[];
}

/** Which form array a field id maps to — one place, so the two cannot drift. */
function valuesFor(field: CampaignCopyFieldId, values: CampaignCopyValues): string[] {
  switch (field) {
    case 'meta_body':
      return values.metaBodyCopy;
    case 'meta_headline':
      return values.metaHeadlines;
    case 'meta_description':
      return values.metaDescriptions;
    case 'google_headline':
      return values.googleHeadlines;
    case 'google_description':
      return values.googleDescriptions;
  }
}

function existingPayload(values: CampaignCopyValues) {
  return {
    meta_body: values.metaBodyCopy,
    meta_headline: values.metaHeadlines,
    meta_description: values.metaDescriptions,
    google_headline: values.googleHeadlines,
    google_description: values.googleDescriptions,
  };
}

// ---------------------------------------------------------------------------
// Trigger
// ---------------------------------------------------------------------------

export function CampaignCopyTrigger({
  brandAccountId,
  onOpen,
  className,
}: {
  brandAccountId: string;
  onOpen: () => void;
  className?: string;
}) {
  const noBrand = brandAccountId.trim() === '';
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <Button type="button" variant="outline" size="sm" onClick={onOpen} disabled={noBrand}>
        <Sparkles className="h-3.5 w-3.5" />
        Generate Copy with AI
      </Button>
      {noBrand && (
        // ⚠️ The brand is not optional context — it resolves the voice profile
        // AND the merge-token length budget. Say why rather than disabling a
        // button with no explanation.
        <span className="text-xs text-slate-500">
          Pick a brand first — the copy follows that brand&apos;s voice and its suburb lengths.
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reporting what was read
// ---------------------------------------------------------------------------

/**
 * ⚠️⚠️ BOTH SIDES, ALWAYS. The positive case is not decoration: showing the
 * extracted LENGTH is what makes a bad read visible — a review that comes back
 * as "read 180 characters" is obviously wrong at a glance where "read 1
 * reference" is not. And an offer PDF we FAILED to read is the one that matters
 * most in this feature: copy written without the offer mechanics, unannounced,
 * looks exactly like copy written with them.
 */
function MaterialReport({ result }: { result: Pick<
  CampaignCopyResult,
  'readReferences' | 'skippedReferences' | 'readDocuments' | 'skippedDocuments'
> }) {
  const nothing =
    result.readReferences.length === 0 &&
    result.skippedReferences.length === 0 &&
    result.readDocuments.length === 0 &&
    result.skippedDocuments.length === 0;
  if (nothing) return null;

  return (
    <div className="space-y-0.5">
      {result.readDocuments.map((doc) => (
        <p key={doc.source} className="text-xs text-slate-600">
          Read <span className="font-medium">{doc.title}</span> —{' '}
          {Math.max(1, Math.round(doc.bytes / 1024)).toLocaleString()} KB.
        </p>
      ))}
      {result.readReferences.length > 0 && (
        <p className="text-xs text-slate-600">
          Read {result.readReferences.length}{' '}
          {result.readReferences.length === 1 ? 'page' : 'pages'} —{' '}
          {result.readReferences
            .map((ref) => `${ref.chars.toLocaleString()} characters`)
            .join(', ')}
          .
        </p>
      )}
      {[...result.skippedDocuments, ...result.skippedReferences].map((skip) => (
        <p key={skip.url} className="text-xs text-amber-700">
          <AlertTriangle className="mr-1 inline h-3 w-3" />
          Couldn&apos;t read <span className="font-medium break-all">{skip.url}</span> —{' '}
          {skip.reason}.
        </p>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ⚠️⚠️ THE OFFER MATERIAL — read once, edited by the AM, reused for ever
// ---------------------------------------------------------------------------

/**
 * A measured 30-page offer toolkit is 58,714 input tokens, and documents ride
 * outside the prompt cache — so before this existed, every generation and every
 * refinement re-read the whole PDF.
 *
 * ⚠️ THE COPY HERE LEADS WITH THE CORRECTNESS ARGUMENT, NOT THE COST ONE, because
 * that is the one that matters to an AM: a pricing tier misread out of thirty
 * pages is corrected ONCE, here, and every generation afterwards is right.
 *
 * ⚠️ AND IT NO LONGER ASKS THEM TO PRESS ANYTHING. The first generation reads the
 * document itself (see startGenerate), so the pre-read copy explains what is
 * about to happen rather than warning about a cost they would have paid by doing
 * nothing. The button stays for reading EARLY, and for re-reading when the offer
 * changes — the one case that must never be automatic.
 */
function OfferMaterial({
  driveLinks,
  offerFacts,
  stale,
  report,
  busy,
  error,
  onRead,
  onEdit,
}: {
  driveLinks: string[];
  offerFacts: CampaignOfferFacts | null;
  stale: boolean;
  report: Awaited<ReturnType<typeof distilOfferFacts>> | null;
  busy: boolean;
  error: string | null;
  onRead: () => void;
  onEdit: (text: string) => void;
}) {
  // Nothing to read and nothing read — say nothing rather than showing an empty
  // control whose purpose is not yet apparent.
  if (driveLinks.length === 0 && !offerFacts) return null;

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <FileText className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        <span className="text-xs font-medium text-slate-700">Offer mechanics</span>
        <span className="flex-1" />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onRead}
          disabled={busy || driveLinks.length === 0}
        >
          {busy ? <Spinner className="h-3.5 w-3.5" /> : null}
          {offerFacts ? 'Re-read the document' : 'Read the offer material'}
        </Button>
      </div>

      {offerFacts ? (
        <>
          {/* ⚠️ EDITABLE, and that is the point of the whole design. */}
          <Textarea
            aria-label="Offer mechanics"
            value={offerFacts.text}
            rows={8}
            onChange={(e) => onEdit(e.target.value)}
          />
          <p className="mt-1 text-[11px] text-slate-500">
            {describeOfferFacts(offerFacts)} Every generation and every refinement works from
            <strong className="font-medium"> this text</strong>, not the document — so if anything
            here is wrong, fix it once and it stays fixed.
          </p>
          {/* ⚠️ Surfaced, not acted on. Re-reading is the AM's decision; doing it
              automatically because a link changed is the silent re-bill this
              design exists to refuse. */}
          {stale && (
            <p className="mt-1 text-[11px] text-amber-700">
              <AlertTriangle className="mr-1 inline h-3 w-3" />
              Your document links have changed since this was read. It is still what the AI will
              use — re-read the document if the offer has changed.
            </p>
          )}
        </>
      ) : (
        <p className="text-[11px] text-slate-500">
          Your first generation will read this document once and turn it into an editable summary
          of its mechanics — prices, dates, tiers, conditions — that every generation afterwards
          works from. Read it now if you would rather check the details before any copy is
          written.
        </p>
      )}

      {error && <p className="mt-1 text-xs text-amber-700">{error}</p>}

      {/* ⚠️ The named skip the AM already knows, on the same path as before — the
          one thing that must never happen here is a silent fall back to
          attaching the raw PDF. */}
      {report?.skippedDocuments.map((skip) => (
        <p key={skip.url} className="mt-1 text-xs text-amber-700">
          <AlertTriangle className="mr-1 inline h-3 w-3" />
          Couldn&apos;t read <span className="font-medium break-all">{skip.url}</span> —{' '}
          {skip.reason}.
        </p>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One option
// ---------------------------------------------------------------------------

function OptionCard({
  option,
  budget,
  values,
  onInsert,
  inserted,
}: {
  option: CampaignCopyOption;
  budget: CampaignCopyResult['budget'];
  values: string[];
  onInsert: (replaceIndex?: number) => void;
  inserted: boolean;
}) {
  const [choosing, setChoosing] = useState(false);
  const plan = planCampaignCopyInsert(option.field, values, option.text);
  const tight = optionIsTight(option);

  return (
    <article
      className={cn(
        'flex flex-col rounded-lg border bg-white p-3',
        inserted ? 'border-kyck-400 ring-1 ring-kyck-200' : 'border-slate-200',
      )}
    >
      <div className="mb-1.5 flex items-baseline gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-kyck-700">
          {campaignCopyLensLabel(option.lens)}
        </span>
        <span className="text-[11px] text-slate-400">{campaignCopyLensBlurb(option.lens)}</span>
      </div>

      <p className="flex-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
        {option.text}
      </p>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {plan.atCapacity && !choosing ? (
          // ⚠️ AT CAPACITY NOTHING IS WRITTEN. An insert that silently dropped
          // entry 3 would be discovered later, with the original gone.
          <Button type="button" size="sm" variant="outline" onClick={() => setChoosing(true)}>
            Replace one…
          </Button>
        ) : plan.atCapacity ? null : (
          <Button
            type="button"
            size="sm"
            variant={inserted ? 'default' : 'outline'}
            onClick={() => onInsert()}
          >
            {inserted ? 'Inserted' : 'Insert'}
          </Button>
        )}
        <span
          className={cn(
            'text-[11px]',
            // ⚠️ The RESOLVED figure leads. "29 characters" on a headline with a
            // merge token looks safely under 30 and is exactly the number that
            // misleads.
            tight ? 'font-medium text-amber-700' : 'text-slate-400',
          )}
        >
          {describeOptionLength(option, budget)}
          {tight ? ' — only just fits' : ''}
        </span>
      </div>

      {choosing && (
        <div className="mt-2 space-y-1 rounded-md border border-amber-200 bg-amber-50 p-2">
          <p className="text-[11px] text-amber-800">
            This field is full. Choose which entry this replaces — nothing is changed until you
            do.
          </p>
          {values.map((existing, index) => (
            <button
              key={`${index}-${existing.slice(0, 12)}`}
              type="button"
              className="block w-full truncate rounded border border-amber-200 bg-white px-2 py-1 text-left text-[11px] text-slate-700 hover:border-amber-400"
              onClick={() => {
                onInsert(index);
                setChoosing(false);
              }}
            >
              {describeReplacement(existing)}
            </button>
          ))}
          <button
            type="button"
            className="text-[11px] text-slate-500 hover:text-slate-800"
            onClick={() => setChoosing(false)}
          >
            Cancel
          </button>
        </div>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// The workspace
// ---------------------------------------------------------------------------

export function CampaignCopyWorkspace({
  open,
  onOpenChange,
  brandAccountId,
  campaignName,
  campaignDescription,
  campaignDescriptor,
  assetLabels,
  values,
  onInsert,
  offerFacts,
  onOfferFactsChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandAccountId: string;
  campaignName: string;
  campaignDescription: string | null;
  campaignDescriptor: string | null;
  /** The campaign's Drive-backed creatives — the AM picks which one the copy is for. */
  assetLabels: AssetLabel[];
  /** ⚠️ THE LIVE FORM VALUES, so capacity reflects what is on screen. */
  values: CampaignCopyValues;
  /** Writes the chosen copy back into the form. Never saves. */
  onInsert: (field: CampaignCopyFieldId, next: string[]) => void;
  /**
   * ⚠️ The distilled offer mechanics, held in the CAMPAIGN FORM — so it survives
   * closing this modal and is persisted by the ordinary campaign save, exactly
   * like inserted copy. Nothing here writes to the server.
   */
  offerFacts: CampaignOfferFacts | null;
  onOfferFactsChange: (facts: CampaignOfferFacts | null) => void;
}) {
  const [brief, setBrief] = useState('');
  const [tone, setTone] = useState('');
  const [links, setLinks] = useState<string[]>(['']);
  const [assetId, setAssetId] = useState<string | null>(null);
  const [fields, setFields] = useState<CampaignCopyFieldId[]>([...COPY_FIELD_ORDER]);
  const [result, setResult] = useState<CampaignCopyResult | null>(null);
  const [insertedIds, setInsertedIds] = useState<string[]>([]);
  const [refineField, setRefineField] = useState<CampaignCopyFieldId | null>(null);
  const [refineText, setRefineText] = useState('');
  const [instruction, setInstruction] = useState('');
  const [refined, setRefined] = useState<CampaignCopyOption | null>(null);
  const [distilReport, setDistilReport] = useState<Awaited<
    ReturnType<typeof distilOfferFacts>
  > | null>(null);

  const trimmedLinks = links.map((l) => l.trim()).filter((l) => l !== '');
  const driveLinks = trimmedLinks.filter(isDriveLink);
  const stale = offerFactsAreStale(offerFacts, driveLinks);

  /**
   * The offer block a generation should carry, when it differs from the prop.
   *
   * ⚠️ A CHAINED FIRST READ CANNOT WAIT FOR STATE. `onOfferFactsChange` lifts the
   * block into the campaign form, and that re-render has not happened by the time
   * the very next line fires the generation — so the freshly-distilled text is
   * passed straight through rather than read back off a stale prop.
   */
  type OfferOverride = { facts: string | null; distilFailed?: boolean };

  const payload = (offer?: OfferOverride) => ({
    brandAccountId,
    campaign: {
      name: campaignName,
      description: campaignDescription,
      descriptor: campaignDescriptor,
    },
    brief,
    tone,
    fields,
    existing: existingPayload(values),
    referenceUrls: trimmedLinks,
    assetDriveFileId: assetId,
    // ⚠️ THE AM'S EDITED TEXT IS WHAT REACHES THE MODEL — not the original
    // distillation. If they corrected a misread pricing tier, that correction is
    // the fact every generation afterwards works from.
    offerFacts: offer ? offer.facts : offerFacts?.text ?? null,
    // ⚠️ Only ever true after a read was tried and failed — see requests.ts. It
    // stops the server falling back to the raw PDF, which would hand the AM their
    // copy while silently restoring the 58,714 tokens.
    offerDistilFailed: offer?.distilFailed ?? false,
  });

  // ⚠️⚠️ ALWAYS THE AM'S OWN CLICK. This is the 58,714-token call, and it fires
  // from exactly two places: the "Read the offer material" button, and the FIRST
  // generation of a campaign that has no block yet (see startGenerate). Both are
  // a click. Reopening the workspace, or editing a link, must never silently
  // re-bill it — which is why there is no effect anywhere in this file.
  const distil = useMutation({
    mutationFn: () =>
      distilOfferFacts({
        brandAccountId,
        campaignName,
        referenceUrls: trimmedLinks,
      }),
    onSuccess: (data) => {
      setDistilReport(data);
      // ⚠️ A read that produced nothing leaves the existing block ALONE. The
      // skips explain why; silently clearing an AM's reviewed facts because one
      // link failed would be the worst outcome available here.
      if (data.facts) onOfferFactsChange(data.facts);
    },
  });

  const generate = useMutation({
    mutationFn: (offer?: OfferOverride) => generateCampaignCopy(payload(offer)),
    onSuccess: (data) => {
      setResult(data);
      setInsertedIds([]);
    },
  });

  /**
   * ⚠️⚠️ THE FIRST READ IS AUTOMATIC; RE-READING IS NOT.
   *
   * Before this, the cheap path was opt-in behind a button nobody was prompted to
   * press, so the EXPENSIVE path was the default: an AM who pasted a link and hit
   * Generate re-sent the whole PDF on that generation and on every one after it.
   * Chaining costs the same single read it always did, but the result is stored,
   * reviewable and editable — so every later generation is cheap and works from
   * the version a human checked.
   *
   * ⚠️ IT NEVER BLOCKS. Whatever the read does, a generation follows: the AM
   * clicked Generate and must get copy, with no new button to learn.
   *   · facts read      → generate from them.
   *   · nothing readable → generate anyway; the server's own fetch fails the same
   *     way and reports the same named skips, so the offer is simply absent.
   *   · the read FAILED  → generate anyway, with documents suppressed. ⚠️ NEVER a
   *     silent fall back to the raw PDF; the distil panel already says why.
   *
   * ⚠️ Chaining happens in a MUTATION CALLBACK — still the AM's own action — which
   * is what keeps this file free of effects and the on-demand guard at its
   * strongest. It is the caption drafter's clarify-then-draft shape exactly.
   *
   * ⚠️ TWO REQUESTS, NOT ONE. Doing the read inside `draftCampaignCopy` would
   * stack a Drive fetch and a 58k-token call in front of an Opus run that already
   * reaches 60-70s against the ~100s Cloudflare origin timeout — the request would
   * die at the proxy while Cloud Run kept working, and the AM would lose the copy
   * they waited two minutes for. Separately, each stays well inside the budget.
   */
  function startGenerate() {
    if (offerFacts || driveLinks.length === 0) {
      generate.mutate(undefined);
      return;
    }
    distil.mutate(undefined, {
      onSuccess: (data) => generate.mutate({ facts: data.facts?.text ?? null }),
      onError: () => generate.mutate({ facts: null, distilFailed: true }),
    });
  }

  // ⚠️ STATELESS: each refine sends the current text plus one instruction, never
  // an accumulating conversation — cost stays flat however many rounds the AM
  // wants, and every round stays grounded in the original brief and offer
  // document rather than in the model's own previous output.
  const refine = useMutation({
    mutationFn: () =>
      refineCampaignCopy({
        ...payload(),
        refineField: refineField ?? 'meta_body',
        currentText: refineText,
        instruction,
      }),
    onSuccess: (data) => {
      // ⚠️ THE REVISION STAYS IN THE REFINE PANEL rather than being prepended to
      // the options list. A revision is no longer "an option written through the
      // urgency lens" — it is whatever the AM asked for — and putting it back in
      // the grid would label it with a lens it does not have, which is a small
      // lie the AM has no way to spot. It also keeps the loop obvious: refine
      // again from here, or insert from here.
      setRefined(data.option);
      setRefineText(data.option.text);
      setInstruction('');
    },
  });

  const busy = generate.isPending || distil.isPending;
  // ⚠️ TWO CLOCKS, NOT ONE. A chained run is a read and then a write, and each
  // has its own honest expectation; one clock spanning both would announce that
  // the writing was "longer than usual" purely because the reading had taken
  // thirty seconds first.
  const readingFor = useElapsedSeconds(distil.isPending);
  const writingFor = useElapsedSeconds(generate.isPending);
  const waitLabel = distil.isPending
    ? copyWaitLabel(readingFor, 'reading')
    : copyWaitLabel(writingFor, 'writing');
  const generateError = generate.isError ? (generate.error as Error).message : null;
  const refineError = refine.isError ? (refine.error as Error).message : null;

  const groups = result ? groupOptionsByField(result.options) : [];

  function insert(option: CampaignCopyOption, replaceIndex?: number) {
    const current = valuesFor(option.field, values);
    const plan = planCampaignCopyInsert(option.field, current, option.text, replaceIndex);
    if (!plan.appended && plan.replaced === null) return; // at capacity, nothing chosen
    onInsert(option.field, plan.next);
    setInsertedIds((ids) => [...ids, option.id]);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[96vw] max-w-6xl flex-col gap-0 overflow-hidden p-0 sm:max-w-6xl">
        <div className="flex items-center gap-2 border-b border-slate-200 px-5 py-3">
          <Sparkles className="h-4 w-4 shrink-0 text-kyck-600" />
          <h2 className="text-sm font-semibold text-slate-800">Generate campaign copy with AI</h2>
          <span className="hidden text-xs text-slate-500 sm:inline">
            — six approaches per field; insert the ones you want
          </span>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {/* ------------- 1 · Inputs ------------- */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div>
              <label
                className="mb-1 block text-xs font-medium text-slate-600"
                htmlFor="copy-brief"
              >
                What is this campaign offering?
              </label>
              <Textarea
                id="copy-brief"
                value={brief}
                rows={5}
                placeholder="6 weeks free when you join in April. No joining fee. Ends 30 April."
                onChange={(e) => setBrief(e.target.value)}
              />
              <p className="mt-1 text-[11px] text-slate-500">
                Anything you state here can be used as fact. Prices, terms and end dates will
                <strong className="font-medium"> only</strong> appear if you put them here, in a
                linked page, or in the offer PDF.
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label
                  className="mb-1 block text-xs font-medium text-slate-600"
                  htmlFor="copy-tone"
                >
                  Tone <span className="font-normal text-slate-400">(optional)</span>
                </label>
                <input
                  id="copy-tone"
                  type="text"
                  value={tone}
                  placeholder="Direct and local, no hype"
                  className="block h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
                  onChange={(e) => setTone(e.target.value)}
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  Changes how it is written, never what may be claimed — a superlative stays
                  blocked however it is asked for.
                </p>
              </div>

              <div>
                <span className="mb-1 block text-xs font-medium text-slate-600">
                  Offer links{' '}
                  <span className="font-normal text-slate-400">
                    (a page, or a Drive PDF of the offer mechanics)
                  </span>
                </span>
                <div className="space-y-1.5">
                  {links.map((link, index) => (
                    <div key={index} className="flex items-center gap-1.5">
                      <Link2 className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                      <input
                        type="url"
                        value={link}
                        placeholder="https://…  offer page, or a Google Drive PDF"
                        className="block h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
                        onChange={(e) =>
                          setLinks((current) =>
                            current.map((l, i) => (i === index ? e.target.value : l)),
                          )
                        }
                      />
                      {links.length > 1 && (
                        <button
                          type="button"
                          title="Remove this link"
                          className="shrink-0 text-slate-400 hover:text-slate-700"
                          onClick={() => setLinks((c) => c.filter((_, i) => i !== index))}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                  {links.length < MAX_REFERENCE_LINKS && (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-kyck-700"
                      onClick={() => setLinks((c) => [...c, ''])}
                    >
                      <Plus className="h-3 w-3" />
                      Add another link
                    </button>
                  )}
                </div>
                {/* ⚠️ Stated where the AM pastes the link, not discovered after a
                    failed run: a PDF in someone's personal Drive is unreadable
                    to us however valid the link looks. */}
                <p className="mt-1 text-[11px] text-slate-500">
                  A Drive PDF has to sit in a folder shared with the agency service account, or we
                  can&apos;t open it — we&apos;ll say so if that happens.
                </p>
              </div>

              <OfferMaterial
                driveLinks={driveLinks}
                offerFacts={offerFacts}
                stale={stale}
                report={distilReport}
                busy={distil.isPending}
                error={distil.isError ? (distil.error as Error).message : null}
                onRead={() => distil.mutate()}
                onEdit={(text) =>
                  onOfferFactsChange(offerFacts ? { ...offerFacts, text } : null)
                }
              />

              {assetLabels.length > 0 && (
                <div>
                  <span className="mb-1 block text-xs font-medium text-slate-600">
                    Creative <span className="font-normal text-slate-400">(optional)</span>
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {assetLabels.slice(0, 12).map((asset) => (
                      <button
                        key={asset.driveFileId}
                        type="button"
                        onClick={() =>
                          setAssetId((current) =>
                            current === asset.driveFileId ? null : asset.driveFileId,
                          )
                        }
                        className={cn(
                          'inline-flex max-w-[14rem] items-center gap-1 truncate rounded-full border px-2 py-0.5 text-[11px]',
                          assetId === asset.driveFileId
                            ? 'border-kyck-400 bg-kyck-50 text-kyck-700'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-kyck-300',
                        )}
                      >
                        <ImageIcon className="h-3 w-3 shrink-0" />
                        <span className="truncate">{asset.label || asset.driveFileName}</span>
                      </button>
                    ))}
                  </div>
                  <p className="mt-1 text-[11px] text-slate-500">
                    Sets the subject and mood. Nothing is ever claimed as fact because it appeared
                    in a picture.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* ------------- 2 · Which fields ------------- */}
          <div className="mt-4">
            <span className="mb-1 block text-xs font-medium text-slate-600">Write copy for</span>
            <div className="flex flex-wrap gap-1.5">
              {CAMPAIGN_COPY_FIELDS.map((field) => {
                const on = fields.includes(field.id);
                return (
                  <button
                    key={field.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() =>
                      setFields((current) =>
                        on ? current.filter((f) => f !== field.id) : [...current, field.id],
                      )
                    }
                    className={cn(
                      'rounded-full border px-2.5 py-0.5 text-[11px] transition-colors',
                      on
                        ? 'border-kyck-400 bg-kyck-50 text-kyck-700'
                        : 'border-slate-200 bg-white text-slate-500 hover:border-kyck-300',
                    )}
                  >
                    {field.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button
              type="button"
              onClick={startGenerate}
              disabled={busy || fields.length === 0}
            >
              {busy ? <Spinner className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
              {result ? 'Generate again' : 'Generate copy'}
            </Button>
            {busy && <span className="text-xs text-slate-500">{waitLabel}</span>}
            {fields.length === 0 && (
              <span className="text-xs text-slate-500">Pick at least one field.</span>
            )}
            {generateError && <span className="text-xs text-amber-700">{generateError}</span>}
          </div>

          {/* ------------- 3 · Options ------------- */}
          {result && (
            <div className="mt-5 space-y-5 border-t border-slate-200 pt-4">
              {groups.map((group) => {
                const field = campaignCopyField(group.field);
                const capacity = result.capacity.find((c) => c.field === group.field);
                return (
                  <section key={group.field}>
                    <div className="mb-2 flex flex-wrap items-baseline gap-2">
                      <h3
                        data-testid={`copy-group-${group.field}`}
                        className="text-sm font-semibold text-slate-800"
                      >
                        {field.label}
                      </h3>
                      {capacity?.fullMessage ? (
                        // ⚠️ SAID, not left as silence — an empty panel after a
                        // click reads as broken.
                        <span className="text-xs text-amber-700">{capacity.fullMessage}</span>
                      ) : (
                        <span className="text-xs text-slate-500">
                          Room for {capacity?.capacity ?? 0} more
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                      {group.options.map((option) => (
                        <OptionCard
                          key={option.id}
                          option={option}
                          budget={result.budget}
                          values={valuesFor(option.field, values)}
                          inserted={insertedIds.includes(option.id)}
                          onInsert={(replaceIndex) => insert(option, replaceIndex)}
                        />
                      ))}
                    </div>
                    <button
                      type="button"
                      className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-kyck-700"
                      onClick={() => {
                        setRefineField(group.field);
                        setRefineText(group.options[0]?.text ?? '');
                        setRefined(null);
                      }}
                    >
                      <Wand2 className="h-3 w-3" />
                      Refine one of these
                    </button>
                  </section>
                );
              })}

              {/* ⚠️⚠️ REFUSED OPTIONS ARE SHOWN, NOT SWALLOWED. A thin set of
                  options with no explanation reads as the AI having little to
                  say; the real reason is usually a merge token that would not
                  have fitted, which is worth an AM knowing. */}
              {result.rejected.length > 0 && (
                <details className="rounded-md border border-slate-200 bg-slate-50 p-3">
                  <summary className="cursor-pointer text-xs font-medium text-slate-700">
                    {result.rejected.length === 1
                      ? '1 option was rejected before you saw it'
                      : `${result.rejected.length} options were rejected before you saw them`}
                  </summary>
                  <ul className="mt-2 space-y-1.5">
                    {result.rejected.map((rejection, index) => (
                      <li key={index} className="text-[11px] text-slate-600">
                        <span className="font-medium">
                          {campaignCopyField(rejection.field).label}:
                        </span>{' '}
                        <span className="italic">&ldquo;{rejection.text}&rdquo;</span> —{' '}
                        {rejection.reason}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              <div className="space-y-1">
                <MaterialReport result={result} />
                <p className="text-xs text-slate-500">
                  {describeBudget(result.budget)}{' '}
                  {result.voiceProfile === 'brand'
                    ? "Written in this brand's voice profile."
                    : 'This brand has no voice profile yet — add one on the brand account to steer future copy.'}{' '}
                  Check every fact: the AI is told never to invent prices, terms or dates, but it
                  can still be wrong.
                </p>
              </div>
            </div>
          )}

          {/* ------------- 4 · Refine ------------- */}
          {refineField && (
            <div className="mt-5 border-t border-slate-200 pt-4">
              <div className="mb-1 flex items-baseline gap-2">
                <h3 className="text-sm font-semibold text-slate-800">
                  Refine a {campaignCopyField(refineField).label.toLowerCase()}
                </h3>
                <button
                  type="button"
                  className="text-xs text-slate-500 hover:text-slate-800"
                  onClick={() => setRefineField(null)}
                >
                  Close
                </button>
              </div>
              <Textarea
                aria-label="Copy to refine"
                value={refineText}
                rows={3}
                onChange={(e) => setRefineText(e.target.value)}
              />
              <div className="mt-1.5 flex max-w-2xl items-center gap-1.5">
                <Wand2 className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <input
                  aria-label="How should it change?"
                  type="text"
                  value={instruction}
                  placeholder="punchier · lead with the price · shorter · mention the suburb"
                  className="block h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
                  onChange={(e) => setInstruction(e.target.value)}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  disabled={
                    refine.isPending || instruction.trim() === '' || refineText.trim() === ''
                  }
                  onClick={() => refine.mutate()}
                >
                  {refine.isPending ? <Spinner className="h-3.5 w-3.5" /> : 'Refine'}
                </Button>
              </div>
              <p className="mt-1 text-[11px] text-slate-500">
                Revises the text above, including your own edits. The revision is checked the same
                way — if it would not fit or would carry an unknown merge field, it is refused and
                your copy is left alone.
              </p>
              {refineError && <p className="mt-1 text-xs text-amber-700">{refineError}</p>}
              {refined && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      const current = valuesFor(refined.field, values);
                      const plan = planCampaignCopyInsert(refined.field, current, refined.text);
                      // At capacity the grid's own Replace control is the way in;
                      // silently overwriting from here would be the exact thing
                      // the capacity rule exists to prevent.
                      if (!plan.appended) return;
                      onInsert(refined.field, plan.next);
                    }}
                    disabled={
                      planCampaignCopyInsert(refined.field, valuesFor(refined.field, values), refined.text)
                        .atCapacity
                    }
                  >
                    Insert this revision
                  </Button>
                  <span className="text-[11px] text-slate-400">
                    {describeOptionLength(refined, result?.budget ?? {
                      fromLocations: false,
                      locationCount: 0,
                      longestShortSuburb: null,
                    })}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <span className="mr-auto text-[11px] text-slate-500">
            Inserting adds to the campaign form — nothing is saved until you save the campaign.
          </span>
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
